import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ImageIcon, Loader2 } from 'lucide-react';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallback?: React.ReactNode;
  imgClassName?: string;
}

export const OptimizedImage: React.FC<OptimizedImageProps> = ({ 
  src, 
  alt, 
  className, 
  imgClassName = 'object-cover',
  fallback,
  style,
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);

  // Reset state when src changes to handle dynamic updates and hydration
  React.useEffect(() => {
    setIsLoaded(false);
    setError(false);
  }, [src]);

  const hasValidSrc = src && typeof src === 'string' && src.length > 0;

  return (
    <div className={`relative overflow-hidden ${className}`} style={style}>
      <AnimatePresence>
        {!isLoaded && !error && hasValidSrc && (
          <motion.div 
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-ows-sand/10 animate-pulse flex items-center justify-center"
          >
            <Loader2 className="h-4 w-4 text-ows-teal animate-spin opacity-20" />
          </motion.div>
        )}
      </AnimatePresence>

      {!hasValidSrc || error ? (
        fallback ? (
          <div className="flex items-center justify-center w-full h-full text-center">
            {fallback}
          </div>
        ) : (
          <div className="absolute inset-0 bg-ows-sand/20 flex flex-col items-center justify-center gap-2 p-4 text-center">
            <MessageSquareError className="h-6 w-6 text-ows-dark/20" />
            <span className="text-[8px] font-bold uppercase tracking-widest text-ows-dark/40">Media Offline</span>
          </div>
        )
      ) : (
        <motion.img
          src={src}
          alt={alt}
          initial={{ opacity: 0, filter: 'blur(10px)' }}
          animate={{ 
            opacity: isLoaded ? 1 : 0, 
            filter: isLoaded ? 'blur(0px)' : 'blur(10px)' 
          }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          onLoad={() => setIsLoaded(true)}
          onError={() => {
            console.warn(`OptimizedImage: Failed to load ${src}`);
            setError(true);
          }}
          className={`w-full h-full ${imgClassName} ${isLoaded ? '' : 'invisible'}`}
          {...(props as any)}
        />
      )}
    </div>
  );
};

const MessageSquareError = ({ className }: { className?: string }) => (
  <ImageIcon className={className} />
);
